<template>
	<!-- eslint-disable vue/custom-event-name-casing -->
	<button
		type="button"
		class="base-button"
		:class="[color, status, { disabled: disabled }]"
		:style="[buttonWidth]"
		@click="$emit('onClick')"
		:aria-label="ariaLabel"
		:disabled="disabled"
	>
		<font-awesome-icon
			v-if="status === 'waiting'"
			class="waiting-animation"
			:icon="['far', 'circle-notch']"
		/>
		<font-awesome-icon
			v-if="status === 'success'"
			class="success-animation"
			:icon="['far', 'check']"
		/>
		<span class="button-content">
			<slot />
		</span>
	</button>
</template>

/**
* Base Button
* Props:
* color - (defaults to gray), white, blue
* status - (default none), waiting, success
* width - (default 100%), any valid css width property, for a more responsive approach,
* you could modify width by adding a css class to BaseButton
* ariaLabel - (defalt none), string accessiblity label for button
* disabled - (default null/false), sets button as disabled. The color and status
* props will overide disabled styling.
*/

<script>
export default {
	name: 'BaseButton',
	props: {
		color: String,
		status: String,
		width: String,
		ariaLabel: String,
		disabled: Boolean,
	},
	computed: {
		buttonWidth() {
			return this.width ? { width: this.width } : null;
		},
	},
};
</script>

<style lang="scss" scoped>
.base-button {
	position: relative;
	@include rem('font-size', 16px);
	@include rem('height', 44px);
	width: 100%;
	font-weight: 500;
	border-radius: 6px;
	border: 1px solid $border-color-light;

	&:hover,
	&:active,
	&.active {
		cursor: pointer;
		outline: none;
	}

	&.blue {
		background-color: $brand-blue;
		color: $white;
	}

	&.white {
		background-color: $white;
		color: $brand-blue;
	}

	&.disabled {
		background-color: $b360-color--gray__lighter;
		color: $b360-color--gray__darker-80;
		cursor: not-allowed;
	}

	&.waiting {
		@include rem(font-size, 20px);
		.button-content {
			opacity: 0;
			@include rem('font-size', 16px);
		}

		.waiting-animation {
			position: absolute;
			top: calc(50% - 10px);
			left: calc(50% - 10px);
			@include rem('min-width', 20px);
			animation: spin 1.5s ease-in-out infinite;
			@include rem(font-size, 20px);
		}
	}

	&.success {
		@include rem(font-size, 20px);
		background-color: $success;
		color: $white;
		.button-content {
			opacity: 0;
			@include rem('font-size', 16px);
		}

		.success-animation {
			position: absolute;
			top: calc(50% - 10px);
			left: calc(50% - 10px);
			@include rem('min-width', 20px);
			animation: scaleIn 0.6s cubic-bezier(0.28, 0.84, 0.42, 1);
		}
	}
}
</style>
